import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatBot } from '@/contexts/ChatBotContext'
import { useBackgroundContrast } from '@/hooks/useBackgroundContrast'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickSuggestions } from './QuickSuggestions'
import { quickSuggestions } from './config'
import type { ChatMessage as ChatMessageType, ChatState } from '@brochure/shared'

export function ChatBot() {
  const { t, i18n } = useTranslation('common')
  const { isOpen, setIsOpen } = useChatBot()
  const [messages, setMessages] = useState<ChatMessageType[]>([])
  const [chatState, setChatState] = useState<ChatState>('idle')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const floatingButtonRef = useRef<HTMLButtonElement>(null)
  const background = useBackgroundContrast(floatingButtonRef, { containerRef })
  const isDarkBackground = background === 'dark'

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 400)
    }
  }, [isOpen])

  const generateId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

  const sendMessage = async (content: string) => {
    if (!content.trim() || chatState === 'loading') return

    const userMessage: ChatMessageType = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setChatState('loading')

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          language: i18n.language,
        }),
      })

      const data = await response.json()

      const assistantMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: data.response || data.error || t('chatbot.error'),
        timestamp: new Date(data.timestamp || Date.now()),
      }

      setMessages((prev) => [...prev, assistantMessage])
      setChatState('idle')
    } catch (error) {
      console.error('Chat error:', error)
      setChatState('error')

      const errorMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: t('chatbot.error'),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const handleReset = () => {
    setMessages([])
    setChatState('idle')
  }

  const handleSuggestionSelect = (messageKey: string) => {
    const message = t(messageKey)
    sendMessage(message)
  }

  const isEmpty = messages.length === 0

  return (
    <div
      ref={containerRef}
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6"
      style={{ zIndex: 9999 }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute bottom-[4.5rem] sm:bottom-[5rem] right-0',
              'w-[calc(100vw-2.5rem)] sm:w-[400px] max-w-[400px]',
              'bg-cream border border-argan/30 overflow-hidden',
              'flex flex-col',
              'shadow-[0_30px_80px_-20px_rgba(59,49,46,0.45)]',
              'max-h-[calc(100vh-7rem)] sm:max-h-[640px]'
            )}
          >
            {/* Decorative top hairline */}
            <div className="h-px bg-gradient-to-r from-transparent via-argan to-transparent" />

            {/* Header */}
            <div className="px-6 pt-6 pb-5 bg-cream border-b border-argan/25 relative">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="block w-5 h-px bg-argan-deep/60" />
                    <span className="text-[10px] font-medium tracking-[0.28em] uppercase text-argan-deep">
                      {t('chatbot.eyebrow', 'Concierge')}
                    </span>
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-argan opacity-60" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-argan-deep" />
                    </span>
                  </div>
                  <h3 className="font-display italic text-bruno text-[1.6rem] leading-[1.1] tracking-tight">
                    {t('chatbot.title')}
                  </h3>
                  <p className="mt-1 text-[12px] text-bruno-soft leading-relaxed">
                    {t('chatbot.subtitle')}
                  </p>
                </div>

                {!isEmpty && (
                  <button
                    onClick={handleReset}
                    className="shrink-0 p-1.5 text-bruno-soft hover:text-bruno transition-colors duration-300"
                    aria-label={t('chatbot.newConversation')}
                    title={t('chatbot.newConversation')}
                  >
                    <RotateCcw className="w-4 h-4" strokeWidth={1.25} />
                  </button>
                )}
              </div>
            </div>

            {/* Messages area */}
            <div
              className={cn(
                'flex-1 overflow-y-auto scrollbar-hide relative',
                'bg-gradient-to-b from-cream to-cream-soft/40'
              )}
            >
              {/* Subtle grain overlay for texture */}
              <div className="nashi-grain pointer-events-none" style={{ opacity: 0.03 }} />

              {isEmpty ? (
                <div className="px-6 py-7 flex flex-col h-full">
                  <p className="text-[10px] font-medium tracking-[0.28em] uppercase text-argan-deep mb-4 flex items-center gap-2">
                    <span className="block w-5 h-px bg-argan-deep/60" />
                    {t('chatbot.suggestionsLabel', 'Inizia da qui')}
                  </p>
                  <QuickSuggestions
                    suggestions={quickSuggestions}
                    onSelect={handleSuggestionSelect}
                    disabled={chatState === 'loading'}
                  />
                </div>
              ) : (
                <div className="px-6 py-6 space-y-5">
                  {messages.map((message, idx) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      isFirst={idx === 0 || messages[idx - 1].role !== message.role}
                    />
                  ))}
                  {chatState === 'loading' && (
                    <ChatMessage
                      message={{
                        id: 'typing',
                        role: 'assistant',
                        content: '',
                        timestamp: new Date(),
                      }}
                      isTyping
                      isFirst={messages.length === 0 || messages[messages.length - 1].role !== 'assistant'}
                    />
                  )}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              disabled={chatState === 'loading'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button — editorial elegance */}
      <motion.button
        ref={floatingButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'group relative w-14 h-14 sm:w-[3.75rem] sm:h-[3.75rem] rounded-full flex items-center justify-center',
          'transition-colors duration-500 border',
          'shadow-[0_10px_30px_-8px_rgba(59,49,46,0.4)]',
          isOpen
            ? 'bg-cream border-argan/40 hover:bg-cream-soft'
            : isDarkBackground
              ? 'bg-cream border-argan/40 hover:bg-cream-soft'
              : 'bg-bruno border-bruno hover:bg-argan-deep'
        )}
        aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
      >
        {/* Available pulse — only when closed */}
        {!isOpen && (
          <span className="absolute top-2 right-2 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-argan opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-argan" />
          </span>
        )}

        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <X className="w-5 h-5 text-bruno" strokeWidth={1.25} />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <MessageCircle
                className={cn(
                  'w-5 h-5 sm:w-[1.35rem] sm:h-[1.35rem]',
                  isDarkBackground ? 'text-bruno' : 'text-cream'
                )}
                strokeWidth={1.25}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  )
}
